import { useMemo, useEffect, useCallback } from 'react';

import { useSetState } from 'src/hooks/use-set-state';

import { authService } from 'src/auth/services/authService';

import { setSession } from './session';
import { refreshToken } from './action';
import { AuthContext } from '../auth-context';
import { isValidToken, tokenExpired } from './utils';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from './constant';

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const { state, setState } = useSetState({
    user: null,
    loading: true,
  });

  const setSessionWithExpCheck = useCallback(
    (accessToken, refreshTokenValue) => setSession(accessToken, refreshTokenValue, (exp) => tokenExpired(exp, () => refreshToken(setSessionWithExpCheck))),
    []
  );

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_KEY);
      const userDataString = localStorage.getItem('userData');
  
      if (accessToken && refreshTokenValue && isValidToken(accessToken) && userDataString) {
        setSessionWithExpCheck(accessToken, refreshTokenValue);
        const userData = JSON.parse(userDataString);
        setState({ user: { ...userData, accessToken, refreshToken: refreshTokenValue }, loading: false });
      } else if (refreshTokenValue) {
        // Try to refresh the token if only refresh token is available
        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = await refreshToken(setSessionWithExpCheck);
        const userData = JSON.parse(userDataString);
        setState({ user: { ...userData, accessToken: newAccessToken, refreshToken: newRefreshToken }, loading: false });
      } else {
        setState({ user: null, loading: false });
      }
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
  }, [setState, setSessionWithExpCheck]);

  const checkNeedToUpdate = useCallback(() => state.user?.NeedToUpdate === true, [state.user]);

  const getProfileUpdateUrl = useCallback(() => '/profile?needUpdate=true', []);

  // Cross-tab logout senkronizasyonu için localStorage event listener
  useEffect(() => {
    const handleStorageChange = (event) => {
      // Eğer access token silinirse (logout), diğer tab'ları da senkronize et
      if (event.key === ACCESS_TOKEN_KEY && !event.newValue && event.oldValue) {
        setState({ user: null, loading: false });
      }
    };

    // localStorage değişikliklerini dinle
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [setState]);

  useEffect(() => {
    checkUserSession();
  }, [checkUserSession]);

  // Rest of the component remains the same
  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';
  const status = state.loading ? 'loading' : checkAuthenticated;


    const changeOrganization = useCallback(async (organizationId) => {
    try {
      const response = await authService.changeOrganization(organizationId, state.user?.accessToken);
      
      // Check if response has the expected format
      if (!response.data || !response.data.data) {
        throw new Error('Invalid response format from change organization API');
      }
      
      const { AccessToken, RefreshToken, User } = response.data.data;

      setSessionWithExpCheck(AccessToken, RefreshToken);

      const updatedUser = {
        ...state.user,
        ...User,
        accessToken: AccessToken,
        refreshToken: RefreshToken,
        UserOrganizations: state.user.UserOrganizations.map(org =>
          org.OrganizationId === organizationId ? { ...org, IsDefault: true } : { ...org, IsDefault: false }
        ),
      };
      
      setState({ user: updatedUser });
      localStorage.setItem('userData', JSON.stringify(updatedUser));

      return response;
    } catch (error) {
      console.error('Error changing organization:', error);
      throw error;
    }
  }, [state.user, setState, setSessionWithExpCheck]);

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            role: state.user?.role ?? 'admin',
          }
        : null,
      checkUserSession,
      setSession: setSessionWithExpCheck,
      changeOrganization,
      checkNeedToUpdate,
      getProfileUpdateUrl,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [checkUserSession, setSessionWithExpCheck, changeOrganization, checkNeedToUpdate, getProfileUpdateUrl, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
